import { safeSetItem } from "Util/util";

const suppressErrorList = [
    /^script error\.?$/i, //Generic error text, e.g. for cross-origin violation
    /(^|error: )unspecified error\.?$/i,
    /^uncaught referenceerror: windo is not defined$/i,
    /^resizeobserver loop limit exceeded$/i,
    /quotaexceedederror: failed to execute 'setitem' on 'storage'/i,
    /can't redefine non-configurable property "userAgent"/i, // appears to be a thing caused by FireFox plugins
    // Seems to show up on iOS devices and Safari
    /undefined is not an object \(evaluating '__gCrWeb.edgeImmersiveReaderDOM.retrieveFeatures'\)/i,
    // One off errors / customers writing their own JS
    /Uncaught SyntaxError: Identifier 'something' has already been declared/i, // Observed starting 11/2022
    /ReferenceError: Can't find variable: pgv2/i // Observed starting 12/2022
];

const tinymceRegex = /cannot read properties of (undefined|null) \(reading 'parentNode'\)$/i;
const sceInstallerJsRegex = /cannot read properties of null \(reading 'length'\)/i;
const tinyMceListRegex = /cannot read properties of undefined \(reading 'length'\)/i;
const browserExtensionStackTraceRegex = /(moz-extension:\/\/|chrome-extension:\/\/)/i;
const suppressFunctionList = [
    // known tinymce issue 4414
    (error, stackTrace) => containsErrorAndStackTraceToken(error, stackTrace, tinymceRegex, "tinymce"),
    // SCE installer running their own JS on the project list page
    (error, stackTrace) => containsErrorAndStackTraceToken(error, stackTrace, sceInstallerJsRegex, "<anonymous>:"),
    // TinyMCE list plugin error
    (error, stackTrace) =>
        containsErrorAndStackTraceToken(error, stackTrace, tinyMceListRegex, "tinymce/plugins/lists/plugin.min.js"),
    // Stack trace appears to come from a browser extension
    (error, stackTrace) => stackTraceMatchesRegex(stackTrace, browserExtensionStackTraceRegex)
];

function containsErrorAndStackTraceToken(error, stackTrace, expectedErrorRegex, expectedStackToken) {
    return expectedErrorRegex.test(error) && stackTraceIncludesToken(stackTrace, expectedStackToken);
}

// Stack trace may be array or string. If neither, return false by default so we don't suppress the error
function stackTraceIncludesToken(stackTrace, token) {
    if (!stackTrace) {
        return false;
    }
    if (typeof stackTrace === "string") {
        return stackTrace.includes(token);
    }
    if (Array.isArray(stackTrace)) {
        return stackTrace.some((l) => l.includes(token));
    }
    return false;
}

// Stack trace may be array or string. If neither, return false by default so we don't suppress the error
function stackTraceMatchesRegex(stackTrace, stackTraceRegex) {
    if (!stackTrace) {
        return false;
    }
    if (typeof stackTrace === "string") {
        return stackTraceRegex.test(stackTrace);
    }
    if (Array.isArray(stackTrace)) {
        return stackTrace.some((l) => stackTraceRegex.test(l));
    }
    return false;
}

const maxErrorReportsAllowed = 16;

function getErrorCount() {
    const lastErrorSentString = localStorage.getItem("lastErrorSent");
    if (lastErrorSentString == null) {
        return 0;
    }
    const lastErrorSent = new Date(lastErrorSentString);
    lastErrorSent.setTime(lastErrorSent.getTime() + 24 * 60 * 60 * 1000);
    if (lastErrorSent < new Date()) {
        return 0;
    }

    const errorCountString = localStorage.getItem("errorCount");
    if (errorCountString == null) {
        return 0;
    }
    return parseInt(errorCountString);
}

function getStackTrace(message, url, line, column, error) {
    // Use the stack trace provided by the error object if it is available.
    if (error && error.stack) {
        return [error.stack];
    }
    // Otherwise, create a psuedo stack trace using known location information.
    return [message, "    at (" + url + ":" + line + ":" + column + ")"];
}

function writeErrorToConsole(error, stack) {
    if (console && console.log) {
        console.log("Javascript error\r\n\r\n");
        console.log(error);
        console.log("\r\n\r\n==== Javascript Stack Trace ====\r\n");
        console.log(stack);
    }
}

/**
 * Gets the user's browser. A user agent can be spoofed, so this is not guaranteed
 * to be accurate.
 * @returns Browser string with name and version inferred from the userAgent
 */
function getBrowser() {
    const ua = navigator.userAgent;
    let tem;
    let m = ua.match(/(opera|chrome|safari|firefox|msie|trident|crios(?=\/))\/?\s*(\d+)/i) || [];
    if (/trident/i.test(m[1])) {
        tem = /\brv[ :]+(\d+)/g.exec(ua) || [];
        return "IE " + (tem[1] || "");
    }
    if (m[1] === "Chrome") {
        tem = ua.match(/\b(OPR|Edge)\/(\d+)/);
        if (tem != null) return tem.slice(1).join(" ").replace("OPR", "Opera");
    }
    if (m[1] === "CriOS" && m[2]) {
        // see https://chromium.googlesource.com/chromium/src/+/master/docs/ios/user_agent.md
        return "Chrome iOS " + m[2];
    }
    m = m[2] ? [m[1], m[2]] : [navigator.appName, navigator.appVersion, "-?"];
    if ((tem = ua.match(/version\/(\d+)/i)) != null) m.splice(1, 1, tem[1]);
    return m.join(" ");
}

export function initErrorReporter(reportErrorAction, vueRootApp) {
    navigator.sayswho = getBrowser();
    const prevOnError = window.onerror;

    function shouldSuppress(error, stackTrace) {
        error = error.trim().toLowerCase();
        const suppressableFuncMatch = suppressFunctionList.some((suppressable) => suppressable(error, stackTrace));
        if (suppressableFuncMatch) {
            return true;
        }
        return suppressErrorList.some((suppressable) => {
            return suppressable.test(error);
        });
    }

    function reportError(error, stackTrace) {
        if (shouldSuppress(error, stackTrace)) {
            return; //Don't call the server if the error is in the suppressed list
        }
        let errorReportsCount = getErrorCount();
        if (errorReportsCount >= maxErrorReportsAllowed) {
            return;
        }
        if (error.includes("ResizeObserver")) {
            error += `Window Size: ${window.innerWidth} x ${window.innerHeight}`;
        }
        const urlParams = new URLSearchParams(window.location.search);
        const programId = urlParams.get("ProgramId");
        const data = {
            programId: programId,
            description: error,
            url: window.location.href,
            stack: stackTrace,
            browser: navigator.sayswho
        };
        reportErrorAction(data);
        errorReportsCount++;
        safeSetItem("errorCount", errorReportsCount);
        safeSetItem("lastErrorSent", new Date());
    }

    function onError(message, url, line, column, error) {
        let result = false;
        try {
            // If there was a previous onError handler, fire it.
            if (typeof prevOnError === "function") {
                result = prevOnError.apply(this, arguments);
            }
            const stackTrace = getStackTrace(message, url, line, column, error);
            reportError(message, stackTrace);
        } catch (e) {
            // Something went wrong, log it.
            writeErrorToConsole(e, getStackTrace(message, url, line, column, error));
        }
        return result;
    }

    function eventListener(ev) {
        const eleArray = ["IMG", "SCRIPT", "LINK"];
        const resourceMap = {
            IMG: "Image file ",
            SCRIPT: "JavaScript file ",
            LINK: "CSS file "
        };
        const ele = ev.target;
        if (ele && eleArray.indexOf(ele.tagName) !== -1) {
            const url = ele.tagName === "LINK" ? ele.href : ele.src;
            const message = "Failure loading " + resourceMap[ele.tagName] + url;
            reportError(message, []);
        }
    }

    // Global error handler.
    window.onerror = onError;
    document.addEventListener("error", eventListener, true);
    if (vueRootApp && vueRootApp.config) {
        vueRootApp.pc_externalErrorHandler = null;
        vueRootApp.pc_setSxternalErrorHandler = (handler) => {
            vueRootApp.pc_externalErrorHandler = handler;
        };
        vueRootApp.config.errorHandler = (error, vm, info) => {
            // If error is null, can't do anything meaningful here, and it will just trigger more console errors
            if (error == null) {
                return;
            }

            const nodeName = (vm && vm.$options && vm.$options._componentTag) || "";
            const message = `${nodeName} | ${info} | ${error.toString()}`;
            reportError(message, error.stack);
            writeErrorToConsole(message, error.stack);

            if (vueRootApp.pc_externalErrorHandler && typeof vueRootApp.pc_externalErrorHandler === "function") {
                vueRootApp.pc_externalErrorHandler(error, vm, info);
            }
        };
    }

    // Safari, in Private Browsing Mode, looks like it supports localStorage but all calls to setItem
    // throw QuotaExceededError. We're going to detect this and just silently drop any calls to setItem
    // to avoid the entire page breaking, without having to do a check at each usage of Storage.
    if (typeof localStorage === "object") {
        try {
            localStorage.setItem("localStorageTest", 1);
            localStorage.removeItem("localStorageTest");
        } catch (e) {
            Storage.prototype.setItem = function () {};
        }
    }
}
